import { z } from "zod";

import type { Layout } from "./types";

/* ============================================================ */
/* Zod schema (recursive)                                        */
/* ============================================================ */

const PanelInstanceSchema = z.object({
  id: z.string(),
  panelId: z.string(),
  viewId: z.string().optional(),
  params: z.record(z.unknown()).optional(),
  title: z.string().optional(),
});

const TabGroupSchema = z.object({
  kind: z.literal("tabs"),
  id: z.string(),
  panels: z.array(PanelInstanceSchema),
  activeIndex: z.number().int(),
});

// Split est recursive : un Split peut contenir d'autres Splits.
// On utilise z.lazy pour resoudre la cycle.
const SplitSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    kind: z.literal("split"),
    id: z.string(),
    direction: z.enum(["row", "column"]),
    children: z.array(z.union([SplitSchema, TabGroupSchema])),
    sizes: z.array(z.number()),
  })
);

const LayoutNodeSchema = z.union([SplitSchema, TabGroupSchema]);

const LayoutSchema = z.object({
  version: z.number().int().min(1),
  root: LayoutNodeSchema,
});

/* ============================================================ */
/* Public API                                                    */
/* ============================================================ */

export class LayoutValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: z.ZodIssue[]
  ) {
    super(message);
    this.name = "LayoutValidationError";
  }
}

/** Serialise vers JSON string. */
export function toJSON(layout: Layout): string {
  return JSON.stringify(layout);
}

/** Parse + valide une JSON string. Throws LayoutValidationError si invalide. */
export function fromJSON(json: string): Layout {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new LayoutValidationError("Invalid JSON", []);
  }
  return validate(parsed);
}

/** Valide un objet quelconque comme Layout. Throws si invalide. */
export function validate(input: unknown): Layout {
  const result = LayoutSchema.safeParse(input);
  if (!result.success) {
    throw new LayoutValidationError(
      "Layout failed schema validation",
      result.error.issues
    );
  }
  return result.data as Layout;
}
